import { t } from 'i18next';
import { upperFirst } from 'lodash';

import { ContactGroupIdCell, ContactGroupNameCell } from 'contacts/components/ContactGroupList';
import {
  ContactStatus,
  InvitationStatus,
  InvitationStatusColorMap,
  InvitationStatusLabel,
} from 'contacts/enums';
import { IContact } from 'contacts/types';
import { AvatarNameCell, StatusCell } from 'core/elements';
import { Color } from 'theme/enums';

export const contactColumnDef = ({
  isGlobal = true,
  openContactGroupDetails = () => {
    /* no-op */
  },
  openContactDetails = () => {
    /* no-op */
  },
}: {
  isGlobal?: boolean;
  openContactGroupDetails?: (data: IContact) => void;
  openContactDetails?: (data: IContact) => void;
}) => {
  return [
    {
      colId: 'name',
      headerName: t('headerName'),
      field: 'name',
      filter: 'agTextColumnFilter',
      cellRenderer: AvatarNameCell,
      cellRendererParams: {
        openDetails: openContactDetails,
      },
    },
    ...(isGlobal
      ? [
          {
            colId: 'groupId',
            headerName: t('headerCompanyId'),
            field: 'groupId',
            filter: 'agNumberColumnFilter',
            width: 100,
            cellRenderer: ContactGroupIdCell,
            cellRendererParams: {
              colId: 'groupId',
              openDetails: openContactGroupDetails,
            },
          },
          {
            colId: 'groupName',
            headerName: t('headerCompany'),
            field: 'groupName',
            filter: 'agTextColumnFilter',
            minWidth: 160,
            width: 280,
            cellRenderer: ContactGroupNameCell,
            cellRendererParams: {
              colId: 'groupName',
              openDetails: openContactGroupDetails,
            },
          },
        ]
      : []),
    {
      colId: 'email',
      headerName: t('headerEmail'),
      field: 'email',
      filter: 'agTextColumnFilter',
    },
    {
      colId: 'phone',
      headerName: t('headerPhone'),
      field: 'phone',
      filter: 'agTextColumnFilter',
    },
    {
      colId: 'billingAddressName',
      headerName: t('headerDefaultBillTo'),
      field: 'billingAddress',
      filter: 'agTextColumnFilter',
      valueGetter: ({ data }) => data && data.billingAddress?.name,
    },
    {
      colId: 'shippingAddressName',
      headerName: t('headerDefaultShipTo'),
      field: 'shippingAddress',
      filter: 'agTextColumnFilter',
      valueGetter: ({ data }) => data && data.shippingAddress?.name,
    },
    {
      colId: 'sellingAddressName',
      headerName: t('headerDefaultSoldTo'),
      field: 'sellingAddress',
      filter: 'agTextColumnFilter',
      valueGetter: ({ data }) => data && data.sellingAddress?.name,
    },
    {
      colId: 'ownerName',
      headerName: t('headerOwner'),
      field: 'ownerName',
      filter: 'agTextColumnFilter',
      cellRenderer: AvatarNameCell,
    },
    {
      colId: 'invitationStatus',
      headerName: t('headerAccess'),
      field: 'invitationStatus',
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: ({ value }: { value: InvitationStatus }) => InvitationStatusLabel[value],
        values: Object.values(InvitationStatusLabel),
        defaultToNothingSelected: true,
      },
      cellRenderer: StatusCell,
      cellRendererParams: {
        colorMap: InvitationStatusColorMap,
        labelMap: Object.fromEntries(Object.entries(InvitationStatusLabel)),
      },
    },
    {
      colId: 'deleted',
      headerName: t('headerStatus'),
      field: 'deleted',
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: ({ value }) => t(`label${upperFirst(value)}`),
        values: Object.values(ContactStatus),
        defaultToNothingSelected: true,
      },
      valueGetter: ({ data }) =>
        data && data.deleted ? ContactStatus.DELETED : ContactStatus.ACTIVE,
      cellRenderer: StatusCell,
      cellRendererParams: {
        colorMap: {
          [ContactStatus.ACTIVE]: Color.SUCCESS,
          [ContactStatus.DELETED]: Color.DANGER,
        },
      },
      cellClass: 'noDecorationCell',
    },
  ];
};
